import * as React from 'react';
import { connect } from 'react-redux';
import * as _ from 'underscore';

import { DialogPositioningBehavior, DialogType } from '@tableau/widgets';

import { useIsMounted } from '~src/app/hooks';
import { AppState } from '~src/app/state/AppState';
import { MapStateToProps } from '~src/app/utils/ConnectUtils';
import { ExplorerOpenDetailEvent, TelemetryEvents } from '~src/common/telemetryEvents/TelemetryEvents';
import { CreateExplorerEvent } from '~src/common/telemetryEvents/TelemetryEventCreator';
import { ModalInstance, ReactModalOptions, ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { Telemetry } from '~src/commonInfra/services/Telemetry';
import { getContentSpaceDefinition } from '~src/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemType, isSpaceInfoItemType } from '~src/ts/models/ContentType';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { IDatasourceActions } from '~src/ts/ServerApi/types/IDatasourceActions';
import { IProjectActions } from '~src/ts/ServerApi/types/IProjectActions';
import { IWorkbookActions } from '~src/ts/ServerApi/types/IWorkbookActions';

const DIALOG_WIDTH = TabDialogWidth.width700px;
export const ACTION_DIALOG_TESTID = 'content-detail-dialog';
export const ACTION_DIALOG_ID = 'content-detail-dialog';

export namespace ContentDetailsDialog {

  interface ContentsOwnProps {
    readonly children: React.ReactNode;
    readonly confirmCloseDialog: () => void;
  }

  interface ContentsStateProps {
    readonly routerReactKey: number;
  }

  type ContentsProps = ContentsOwnProps & ContentsStateProps;

  const Contents = (props: ContentsProps) => {
    const isMounted = useIsMounted();
    React.useEffect(() => {
      if (isMounted) {
        // The routerReactKey is used in high level HashRouters and triggers rerenders if key changes.
        // We use this to refresh all content in a page without doing a page reload.
        // So we know if an action change was successful if this key has changed and so close the dialog.
        props.confirmCloseDialog();
      }
    }, [props.routerReactKey]);

    return (
      <div style={{display: 'flex', flexDirection: 'column'}}>
       {props.children}
      </div>
    );
  };

  const mapStateToProps: MapStateToProps<ContentsStateProps> = (state: AppState) => ({
    routerReactKey: state.routerSpaceReactKey
  });

  export const ConnectedContents: React.ComponentClass<ContentsOwnProps> =
    connect(mapStateToProps)(Contents);

  interface OptionsProps {
    readonly content: IContentInfo<ExplorableItemType>;
    readonly availableActions: IDatasourceActions|IWorkbookActions|IProjectActions;
    readonly confirmCloseDialog: () => void;
  }
  export function Options(props: OptionsProps): ReactModalOptions {

    const contentType = props.content.contentType;
    let dialogTitle = '';
    let dialogBody: any = undefined;
    if (isSpaceInfoItemType(contentType)) {
      const contentDefinition = getContentSpaceDefinition(contentType);
      dialogTitle = contentDefinition.detailDialogTitle();
      dialogBody = contentDefinition.detailDialogBody;
    }

    if (dialogTitle.length === 0 || _.isUndefined(dialogBody)) {
      throw new Error(contentType + ' is not supported for react detail dialog.');
    }

    return {
      title: dialogTitle,
      testId: ACTION_DIALOG_TESTID,
      type: DialogType.NormalWithTitleBar,
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      dialogWidth: DIALOG_WIDTH,
      dialogStyle: {minWidth: DIALOG_WIDTH + 'px'},
      dialogBodyStyle: {width: DIALOG_WIDTH + 'px'},
      dialogContent:
        <ConnectedContents confirmCloseDialog={props.confirmCloseDialog}>
          {dialogBody(props.content, props.availableActions)}
        </ConnectedContents>,
      dialogContentStyle: { padding: 0 },
    };
  }

  export function open(content: IContentInfo<ExplorableItemType>, availableActions: IDatasourceActions|IWorkbookActions|IProjectActions) {
    let modalInstance: ModalInstance<'confirm'>;

    const confirmCloseDialog = (): void => {
      modalInstance && modalInstance.close('confirm');
    };
    const optionProps: OptionsProps = {
      content,
      availableActions,
      confirmCloseDialog
    };

    Telemetry.sendEvent<ExplorerOpenDetailEvent>(CreateExplorerEvent<ExplorerOpenDetailEvent>(
      {
        type: TelemetryEvents.ExplorerOpenDetail,
        detailContentId: content.id,
        detailContentType: content.contentType
      }
    ));

    modalInstance = ReactModalService.open(Options(optionProps));
    return modalInstance.result;
  }
}
